import { env } from '@ui/environment';
import { getAccessToken, getRefreshToken, setAccessToken } from '@ui/token-storage';
import { axios } from './axios';
import { handleForbiddenRedirect } from './handleRedirect';
const REFRESH_TIMEOUT = 15000;
let isRefreshing = false;
let failedQueue = [];
function processQueue(error, token) {
    failedQueue.forEach((param)=>{
        let { resolve, reject, timeoutId } = param;
        clearTimeout(timeoutId);
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedQueue = [];
}
function clearFailedQueue() {
    failedQueue.forEach((param)=>{
        let { reject, timeoutId } = param;
        clearTimeout(timeoutId);
        reject(new Error('Token refresh timeout'));
    });
    failedQueue = [];
}
axios.interceptors.request.use((config)=>{
    if (!config.headers.Authorization) {
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = "Bearer ".concat(token);
        }
    }
    sessionStorage.removeItem('axiosErrorMessage');
    sessionStorage.removeItem('axiosError');
    return config;
}, (error)=>Promise.reject(error));
axios.interceptors.response.use((response)=>response, async (error)=>{
    var _error_response, _error_response1;
    const originalRequest = error.config;
    const refreshToken = getRefreshToken();
    if (((_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status) === 401 && !originalRequest._retry && refreshToken) {
        if (isRefreshing) {
            return new Promise((resolve, reject)=>{
                // timeouts for each queued promise to automatically reject with a timeout error and avoid memory leaks if promises never resolve
                const timeoutId = setTimeout(()=>{
                    const index = failedQueue.findIndex((item)=>item.timeoutId === timeoutId);
                    if (index > -1) {
                        failedQueue.splice(index, 1);
                        reject(new Error('Token refresh timeout'));
                    }
                }, REFRESH_TIMEOUT);
                failedQueue.push({
                    resolve,
                    reject,
                    timeoutId
                });
            }).then((token)=>{
                originalRequest.headers.Authorization = "Bearer ".concat(token);
                return axios(originalRequest);
            }).catch((err)=>{
                return Promise.reject(err);
            });
        }
        originalRequest._retry = true;
        isRefreshing = true;
        const refreshTimeoutId = setTimeout(()=>{
            clearFailedQueue();
            isRefreshing = false;
        }, REFRESH_TIMEOUT);
        try {
            const refreshUrl = new URL('/api/refresh-token', env.PUBLIC_AUTH_API_BASE_URL);
            refreshUrl.searchParams.set('refresh_token', refreshToken);
            // use fetch here to avoid axios loop requests, because we can get 401 from refresh api also.
            const res = await fetch(refreshUrl.toString(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) {
                throw new Error('Refresh token request failed');
            }
            const data = await res.json();
            clearTimeout(refreshTimeoutId);
            setAccessToken(data.access_token, data.refresh_token);
            processQueue(null, data.access_token);
            originalRequest.headers.Authorization = "Bearer ".concat(data.access_token);
            return await axios(originalRequest);
        } catch (refreshError) {
            clearTimeout(refreshTimeoutId);
            processQueue(refreshError, null);
            // Clear tokens and redirect to login
            setAccessToken(null, null);
            window.location.href = '/login';
            return await Promise.reject(refreshError);
        } finally{
            isRefreshing = false;
        }
    }
    if (((_error_response1 = error.response) === null || _error_response1 === void 0 ? void 0 : _error_response1.status) === 403) {
        var _ref, _ref1;
        var _error_response2;
        const { errorMessage, errorData } = (_ref = (_error_response2 = error.response) === null || _error_response2 === void 0 ? void 0 : _error_response2.data) !== null && _ref !== void 0 ? _ref : {};
        sessionStorage.setItem('axiosErrorMessage', errorMessage !== null && errorMessage !== void 0 ? errorMessage : '');
        sessionStorage.setItem('axiosError', (_ref1 = errorData === null || errorData === void 0 ? void 0 : errorData.error) !== null && _ref1 !== void 0 ? _ref1 : '');
        handleForbiddenRedirect();
    }
    return Promise.reject(error);
});
export function AxiosInterceptor(param) {
    let { children } = param;
    return children;
}
